/** Option A presentation adapter. Cart writes stay in the existing PDP pipeline. */
window.FDUpsellRoutine = class {
  constructor(dialog) {
    this.dialog = dialog;
    this.enabled = false;
    this.stacks = [];
    this.family = [];
    this.demo = "teens";
  }

  read(selector, fallback = {}) {
    try {
      return JSON.parse(document.querySelector(selector)?.textContent || "{}");
    } catch (_) {
      return fallback;
    }
  }

  esc(value) {
    return String(value ?? "").replace(
      /[&<>"']/g,
      (char) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[char],
    );
  }

  money(value) {
    return "$" + (value / 100).toFixed(2);
  }
  price(product) {
    return this.dialog.isSubscription
      ? (product.subscriptionPrice ?? product.price)
      : (product.onetimePrice ?? product.price);
  }
  title(product) {
    return product.customTitle || product.shortName || product.title;
  }

  prepare() {
    const d = this.dialog;
    if (!d.routineChrome)
      d.routineChrome = {
        title: d.querySelector(".upsell-dialog__header-title").innerHTML,
        primary: d.primaryBtnLabel.textContent,
        giftHandle: d.giftSection?.getAttribute("data-info-modal-open"),
        giftTemplate: d.giftSection?.getAttribute("data-info-modal-template"),
      };
    const data = d.routineData || this.read("#PBFCM-routine-data");
    this.config = this.read("[data-routine-config]");
    this.main = data.main;
    const experiment =
      document.documentElement.classList.contains("fd-pdp-upsell-v2") ||
      document.body.classList.contains("fd-pdp-upsell-v2");
    const preview =
      Boolean(window.Shopify?.theme?.role) &&
      window.Shopify.theme.role !== "main" &&
      (d.dataset.routinePreview === "true" ||
        new URLSearchParams(location.search).get("upsell_v2") === "a");
    this.enabled = !!(
      (experiment || preview) &&
      this.main?.handle &&
      (d.routineData ||
        (d.getBaseDemo() === "kids" && /multi|vitamin/i.test(this.main.title)))
    );
    d.classList.toggle("upsell-dialog--routine", this.enabled);
    if (!this.enabled) {
      d.querySelector(".upsell-dialog__header-title").innerHTML =
        d.routineChrome.title;
      d.primaryBtnLabel.textContent = d.routineChrome.primary;
      d.querySelectorAll(
        "[data-routine-gift-toggle], .upsell-routine__gift-info",
      ).forEach((el) => el.remove());
      d.giftSection?.classList.remove("upsell-routine__gift-expanded");
      if (d.routineChrome.giftHandle)
        d.giftSection.setAttribute(
          "data-info-modal-open",
          d.routineChrome.giftHandle,
        );
      if (d.routineChrome.giftTemplate)
        d.giftSection.setAttribute(
          "data-info-modal-template",
          d.routineChrome.giftTemplate,
        );
      d.upsellCardsContainer.onclick = null;
      d.upsellCardsContainer.onkeydown = null;
      return;
    }
    const raw = d.routineData
      ? d.upsellProducts
      : this.read("#PBFCM-upsell-products-data", []);
    this.catalog = window.FDOffer?.active?.replacesGlobal
      ? d.upsellProducts.slice()
      : Array.isArray(raw)
        ? raw
        : d.upsellProducts.slice();
    const kids = this.catalog.filter(
      (p) => d.getUpsellDemo(p) === "kids" && !p.bundleVariants?.length,
    );
    const roles = [
      ["focus", /focus|brain/i, "Focus Support"],
      ["calm", /magnesium/i, "Magnesium"],
      ["biotic", /biotic/i, "Probiotic"],
    ];
    const showStacks =
      d.getBaseDemo() === "kids" && /multi|vitamin/i.test(this.main.title);
    roles.forEach(([key, pattern, label]) => {
      if (!showStacks) return;
      const product = kids.find((p) => pattern.test(p.handle + " " + p.title));
      if (!product) return;
      this.stacks.push(this.stack(key, [product], [label]));
    });
    if (this.stacks.length === 3) {
      this.stacks.push(
        this.stack(
          "complete",
          this.stacks.flatMap((p) => p.components),
          ["Focus Support", "Magnesium", "Probiotic"],
        ),
      );
    }
    const catalog = new Map(
      [...(data.family || []), ...this.catalog].map((p) => [String(p.id), p]),
    );
    this.family = Array.from(catalog.values())
      .filter((p) => !p.bundleVariants?.length)
      .map((p) => {
        // Family cards use the PDP's standard offer, stacks keep its add-on offer.
        const offer = window.FDOffer?.active;
        const percent = d.isSubscription ? offer?.subPct : offer?.otpPct;
        const price =
          offer?.replacesGlobal && Number.isFinite(percent)
            ? Math.round((p.comparePrice * (100 - percent)) / 100)
            : d.getUpsellDemo(p) === "kids"
              ? this.price(p)
              : (p.onetimePrice ?? p.price);
        return {
          ...p,
          id: "family-" + p.id,
          sourceId: p.id,
          subscriptionPrice: price,
          components: [{ ...p, subscriptionPrice: price }],
        };
      });
    const role = (p) =>
      /multi|vitamin/i.test(p.title)
        ? 0
        : /focus|brain/i.test(p.title)
          ? 1
          : /magnesium/i.test(p.title)
            ? 2
            : /biotic/i.test(p.title)
              ? 3
              : -1;
    const demographics = ["kids", "toddler", "teens", "womens", "mens"];
    this.family.sort(
      (a, b) =>
        demographics.indexOf(d.getUpsellDemo(a)) -
          demographics.indexOf(d.getUpsellDemo(b)) || role(a) - role(b),
    );
    d.upsellProducts = [...this.stacks, ...this.family];
    d.querySelector(".upsell-dialog__header-title").textContent =
      this.config.title;
    d.primaryBtnLabel.textContent = this.config.add;
    d.secondaryBtnLabel.textContent = this.config.decline;
    d.upsellDemoTabs.hidden = true;
    d.upsellDemoTabs.innerHTML = "";
    d.upsellDemoEmpty.hidden = true;
    this.prepareGift();
    d.trackEngagement("upsell_dialog_option_a", {
      engagementType: "view",
      clickText: "Option A",
    });
  }

  stack(key, components, labels) {
    const sum = (field) =>
      components.reduce((total, p) => total + Number(p[field] ?? p.price), 0);
    return {
      id: "routine-" + key,
      key,
      title: this.config[key + "Title"],
      description: this.config[key + "Description"],
      subtitle: "Multi + " + labels.join(" + "),
      components,
      labels,
      price: sum("price"),
      subscriptionPrice: sum("subscriptionPrice"),
      onetimePrice: sum("onetimePrice"),
      comparePrice: sum("comparePrice"),
      maxQuantity: 5,
    };
  }

  quantity(product) {
    return `<div class="upsell-dialog__upsell-quantity" role="group" aria-label="${this.esc(product.title)} quantity">
      <button type="button" class="upsell-dialog__qty-btn" data-routine-qty="-1" data-product-id="${product.id}" aria-label="Decrease ${this.esc(product.title)}" disabled><svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 8h10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button>
      <span class="upsell-dialog__qty-display" aria-live="polite">0</span>
      <button type="button" class="upsell-dialog__qty-btn" data-routine-qty="1" data-product-id="${product.id}" aria-label="Increase ${this.esc(product.title)}"><svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 8h10M8 3v10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button>
    </div>`;
  }

  image(product) {
    return product?.imageUrl
      ? `<img src="${this.esc(product.imageUrl)}" alt="${this.esc(this.title(product))}" loading="lazy" width="72" height="72">`
      : "";
  }

  prices(product, stack = false) {
    const price = this.price(product);
    const saving = product.comparePrice - price;
    return `<div class="upsell-routine__prices"><strong>${stack ? "+" : ""}${this.money(price)}</strong>${saving > 0 ? `<s>${this.money(product.comparePrice)}</s>${stack ? `<span>Save ${this.money(saving)}</span>` : ""}` : ""}</div>`;
  }

  render() {
    const d = this.dialog;
    const demos = ["kids", "toddler", "teens", "parents"].filter((demo) =>
      this.family.some((p) => this.familyDemo(p) === demo),
    );
    if (!demos.includes(this.demo)) this.demo = demos[0];
    d.upsellSection.style.display = "";
    d.upsellCardsContainer.innerHTML = `
      ${
        this.stacks.length
          ? `<section class="upsell-routine__stacks" aria-label="Stacks"><h3>${this.esc(this.config.heading)}</h3><p class="upsell-routine__subtitle">${this.esc(this.config.subheading)}</p>${this.stacks
              .map(
                (p) => `
        <article class="upsell-routine__stack" data-product-id="${p.id}">
          ${p.key === "complete" ? `<span class="upsell-routine__best">${this.esc(this.config.bestValue)}</span>` : ""}
          <button type="button" class="upsell-routine__summary" data-routine-details="${p.id}" aria-label="Details ${this.esc(p.title)}">
            <span class="upsell-routine__images">${[this.main, ...p.components].map((item) => this.image(item)).join("")}</span>
            <span><strong>${this.esc(p.title)} <span aria-hidden="true">i</span></strong><small>${this.esc(p.subtitle)}</small></span>
          </button><p>${this.esc(p.description)}</p><div class="upsell-routine__bottom">${this.prices(p, true)}${this.quantity(p)}</div>
        </article>`,
              )
              .join("")}</section>`
          : ""
      }
      <section class="upsell-routine__family"><h3>${this.esc(this.config.familyHeading)}</h3>
        <div class="upsell-routine__tabs" role="tablist" aria-label="Shop for the family">${demos.map((demo) => `<button type="button" role="tab" data-routine-demo="${demo}" aria-selected="${demo === this.demo}" tabindex="${demo === this.demo ? 0 : -1}">${{ kids: "Kids", toddler: "Toddlers", teens: "Teens", parents: "Parents" }[demo]}</button>`).join("")}</div>
        ${this.family
          .map(
            (
              p,
            ) => `<article class="upsell-routine__family-card" data-product-id="${p.id}" data-routine-audience="${this.familyDemo(p)}" ${this.familyDemo(p) !== this.demo ? "hidden" : ""}>
          <button type="button" class="upsell-routine__product-image" data-routine-details="${p.id}" aria-label="Product details ${this.esc(this.title(p))}">${this.image(p)}<span aria-hidden="true">i</span></button>
          <div class="upsell-routine__product-copy"><button type="button" data-routine-details="${p.id}">${this.esc(this.title(p))}</button><p>${this.esc(p.description)}</p>${this.prices(p)}</div>${this.quantity(p)}
        </article>`,
          )
          .join("")}
      </section>`;
    d.upsellCardsContainer.onclick = (event) => {
      const qty = event.target.closest("[data-routine-qty]");
      if (qty) {
        this.changeQuantity(
          qty.dataset.productId,
          Number(qty.dataset.routineQty),
        );
        d.trackEngagement("upsell_dialog_quantity_change", {
          productTitle: d.upsellProducts.find(
            (p) => p.id === qty.dataset.productId,
          )?.title,
        });
      }
      const detail = event.target.closest("[data-routine-details]");
      if (detail) this.openDetails(detail.dataset.routineDetails);
      const tab = event.target.closest("[data-routine-demo]");
      if (tab) this.selectDemo(tab.dataset.routineDemo);
    };
    d.upsellCardsContainer.onkeydown = (event) => {
      const tab = event.target.closest("[data-routine-demo]");
      if (
        !tab ||
        !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)
      )
        return;
      event.preventDefault();
      const tabs = Array.from(
        d.upsellCardsContainer.querySelectorAll("[data-routine-demo]"),
      );
      let index = tabs.indexOf(tab);
      index =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? tabs.length - 1
            : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) %
              tabs.length;
      this.selectDemo(tabs[index].dataset.routineDemo);
      tabs[index].focus();
    };
    this.syncQuantities();
  }

  familyDemo(product) {
    const demo = this.dialog.getUpsellDemo(product);
    return ["mens", "womens"].includes(demo) ? "parents" : demo;
  }

  selectDemo(demo) {
    this.demo = demo;
    this.dialog.querySelectorAll("[data-routine-demo]").forEach((tab) => {
      const active = tab.dataset.routineDemo === demo;
      tab.setAttribute("aria-selected", String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.dialog.querySelectorAll("[data-routine-audience]").forEach((card) => {
      card.hidden = card.dataset.routineAudience !== demo;
    });
  }

  changeQuantity(id, delta) {
    const d = this.dialog;
    if (delta > 0 && id.startsWith("routine-")) {
      this.stacks.forEach((stack) => {
        if (stack.id !== id) delete d.upsellQuantities[stack.id];
      });
    }
    d.updateUpsellQuantity(id, delta);
  }

  syncQuantities() {
    if (!this.enabled) return;
    this.dialog.upsellProducts.forEach((p) => {
      const card = this.dialog.querySelector(`[data-product-id="${p.id}"]`);
      if (!card) return;
      const qty = this.dialog.upsellQuantities[p.id] || 0;
      card.querySelector(".upsell-dialog__qty-display").textContent = qty;
      card.querySelector('[data-routine-qty="-1"]').disabled = qty === 0;
      card.querySelector('[data-routine-qty="1"]').disabled =
        qty >= p.maxQuantity;
    });
  }

  openDetails(id) {
    const d = this.dialog;
    const product = d.upsellProducts.find((p) => p.id === id);
    if (!product || !window.FDInfoModal) return;
    const stack = !!product.key;
    const tabs = stack
      ? [
          { ...this.main, label: "Multi" },
          ...product.components.map((p, i) => ({
            ...p,
            title: this.title(p),
            label: product.labels[i].replace(" Support", ""),
          })),
        ]
      : [];
    const offers = Object.fromEntries(
      product.components.map((p) => [
        p.handle,
        { price: this.price(p), compare: p.comparePrice },
      ]),
    );
    if (stack) offers[this.main.handle] = { hidePrice: true };
    window.FDInfoModal.open(product.components[0].handle, {
      routine: true,
      cta: false,
      offers,
      tabs,
      title: this.title(product),
      footer: {
        label: stack ? `Add ${product.title} to my order` : "Add to my order",
        onClick: () => this.changeQuantity(id, 1),
      },
    });
    d.trackEngagement("upsell_dialog_product_details", {
      productTitle: product.title,
    });
  }

  expandSelection(selection) {
    const expanded = {};
    Object.entries(selection).forEach(([id, item]) => {
      const product = this.dialog.upsellProducts.find((p) => p.id === id);
      product.components.forEach((component) => {
        // Distinct prices/coupons remain distinct lines when a product is both a stack and family add-on.
        const key = `${component.id}-${this.price(component)}-${component.coupon || ""}`;
        if (expanded[key]) {
          expanded[key].quantity += item.quantity;
          return;
        }
        expanded[key] = {
          ...item,
          productId: component.id,
          variantId: component.variantId,
          title: component.title,
          price: this.price(component),
          comparePrice: component.comparePrice,
          sellingPlanId: component.sellingPlanId,
          coupon: component.coupon || "",
          isAddon: component.isAddon || false,
          handle: component.handle,
          bundleVariants: [],
          bundleName: "",
          freeUnlock: component.freeUnlock || null,
        };
      });
    });
    return expanded;
  }

  prepareGift() {
    const d = this.dialog;
    if (!d.giftSection || d.querySelector("[data-routine-gift-toggle]")) return;
    const handle = d.giftSection.getAttribute("data-info-modal-open");
    const template = d.giftSection.getAttribute("data-info-modal-template");
    d.giftSection.removeAttribute("data-info-modal-open");
    d.giftSection.removeAttribute("data-info-modal-template");
    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.dataset.routineGiftToggle = "";
    toggle.className = "upsell-routine__gift-toggle";
    toggle.setAttribute("aria-expanded", "false");
    toggle.setAttribute("aria-label", "Show gift details");
    toggle.innerHTML =
      '<span data-routine-gift-status></span><span aria-hidden="true">⌄</span>';
    d.giftSection.prepend(toggle);
    if (handle || template) {
      const info = document.createElement("button");
      info.type = "button";
      info.className = "upsell-routine__gift-info";
      info.textContent = d.dataset.upsellInfoLabel || "More Info";
      if (handle) info.setAttribute("data-info-modal-open", handle);
      else info.setAttribute("data-info-modal-template", template);
      d.giftDescription?.after(info);
    }
    toggle.onclick = () => {
      const expanded = toggle.getAttribute("aria-expanded") !== "true";
      toggle.setAttribute("aria-expanded", String(expanded));
      toggle.setAttribute(
        "aria-label",
        expanded ? "Hide gift details" : "Show gift details",
      );
      d.giftSection.classList.toggle("upsell-routine__gift-expanded", expanded);
    };
  }

  updateGift(remaining, threshold) {
    const gift = this.dialog.giftConfig?.giftProduct;
    const badge = this.dialog.giftConfig?.badgeOriginalPrice;
    const value = badge
      ? Number(String(badge).replace(/[^0-9.]/g, ""))
      : Number(gift?.comparePrice || gift?.price || 0) / 100;
    const status = this.dialog.querySelector("[data-routine-gift-status]");
    if (status)
      status.textContent =
        remaining > 0
          ? `${this.money(remaining)} away from a FREE ${value ? "$" + value + " " : ""}GIFT!`
          : "🎉 Gift Unlocked";
    if (this.dialog.giftGoal)
      this.dialog.giftGoal.textContent = "$" + threshold / 100;
    const bar = this.dialog.querySelector('[role="progressbar"]');
    if (bar)
      bar.setAttribute(
        "aria-valuenow",
        String(
          Math.min(
            100,
            Math.round(((threshold - remaining) / threshold) * 100),
          ),
        ),
      );
  }
};
